import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  FileText, Clock, CheckCircle2, AlertTriangle, Plus, LogOut,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Intake Started': 'bg-[#94A3B8] text-white',
  'Files Uploaded': 'bg-[#0B1F3F] text-white',
  'Received by VARfacts': 'bg-[#0B1F3F] text-white',
  'In Review': 'bg-[#C5A55A] text-[#0B1F3F]',
  'Clarification Needed': 'bg-amber-100 text-amber-800',
  'Report Drafting': 'bg-[#C5A55A]/20 text-[#0B1F3F]',
  'Final Review': 'bg-[#C5A55A]/40 text-[#0B1F3F]',
  'Delivered': 'bg-emerald-100 text-emerald-800',
  'Closed': 'bg-[#D4D4D0] text-[#64748B]',
};

export default function PortalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchMatters();
  }, [user]);

  const fetchMatters = async () => {
    try {
      const res = await axios.get(`${API}/portal/matters`, { withCredentials: true });
      setMatters(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="portal-dashboard">
      {/* Header */}
      <nav className="bg-white border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/portal" className="flex items-center gap-3">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8] hidden sm:inline">Secure Matter Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#64748B]">{user.name || user.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-[#94A3B8] hover:text-[#0B1F3F]">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">Matter Dashboard</p>
            <h1 className="font-heading text-3xl text-[#0B1F3F] mt-1" data-testid="portal-h1">Active Matters</h1>
          </div>
          <Button asChild className="btn-primary-varfacts px-5 py-2.5 rounded-sm" data-testid="new-matter-btn">
            <Link to="/portal/matters/new">
              <Plus className="w-4 h-4 mr-2" /> New Matter
            </Link>
          </Button>
        </div>

        {loading ? (
          <p className="text-[#94A3B8]">Loading matters...</p>
        ) : matters.length === 0 ? (
          <div className="bg-white border border-[#D4D4D0] rounded-sm p-12 text-center space-y-4">
            <FileText className="w-12 h-12 text-[#D4D4D0] mx-auto" />
            <p className="text-[#64748B]">No matters yet. Create your first matter to begin.</p>
            <Button asChild className="btn-primary-varfacts px-5 py-2.5 rounded-sm">
              <Link to="/portal/matters/new">Create Matter</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="matters-list">
            {matters.map((matter) => (
              <Link
                key={matter.matter_id}
                to={`/portal/matters/${matter.matter_id}`}
                className="block bg-white border border-[#D4D4D0] rounded-sm p-6 hover:border-[#C5A55A] transition-colors"
                data-testid={`matter-card-${matter.matter_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-xs text-[#94A3B8] font-mono">{matter.matter_id}</p>
                    <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">{matter.title}</h3>
                    <p className="text-sm text-[#64748B]">{matter.client_name} &middot; {matter.matter_type}</p>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[matter.status] || 'bg-[#D4D4D0] text-[#64748B]'}`}>
                    {matter.status}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-3">
                  Last updated: {new Date(matter.updated_at).toLocaleDateString()}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
